import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from './../../app/auth/services/auth.service';
import { AccountService } from '../../app/auth/services/account.service';
import { CacheService } from './cache.service';
import { ChunkErrorHandlerService } from './chunk-error-handler.service';
import { AuthModel } from '../../app/auth/models/auth.model';
import { Browser } from '../models/browser';
import { LocalStorageKeysEnum } from '../models/localStorageKeys-enum';
import { localStorageGetItem } from '../utils/safe-storage';

let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

function readBrowserInfo(): Browser | undefined {
  try {
    const raw = localStorageGetItem(LocalStorageKeysEnum.Browser);
    return raw ? (JSON.parse(raw) as Browser) : undefined;
  } catch {
    return undefined;
  }
}

/** Auth + LogSession headers expected by API LogSession (ipaddress, browser, deviceType, os). */
function withAuthAndLogHeaders(req: HttpRequest<any>, token: string): HttpRequest<any> {
  const browser = readBrowserInfo();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  headers['ipaddress'] = browser?.ip || 'Unknown';
  headers['browser'] = browser?.browser || 'Unknown';
  headers['deviceType'] = browser?.deviceType || 'Unknown';
  headers['os'] = browser?.os || 'Unknown';

  if (typeof window !== 'undefined' && window.location?.href) {
    headers['currenturl'] = window.location.href;
  }

  return req.clone({ setHeaders: headers });
}

function isExternal(url: string): boolean {
  return url.includes('blob.core.windows.net') || url.includes('api64.ipify');
}

export const appInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {

  const authService = inject(AuthService);
  const accountService = inject(AccountService);
  const cacheService = inject(CacheService);
  const ngZone = inject(NgZone);
  const chunkErrorHandler = inject(ChunkErrorHandlerService);

  // ---------------------------
  // 1. GET TOKEN (SSR SAFE)
  // ---------------------------
  let token = authService.getAuthFromLocalStorage()?.authToken || '';

  if (!token && typeof document !== 'undefined') {
    const match = document.cookie.match(/authToken=([^;]+)/);
    token = match ? match[1] : '';
  }

  // ---------------------------
  // 2. SKIP EXTERNAL REQUESTS
  // ---------------------------
  if (isExternal(req.url)) {
    return next(req);
  }

  // Warm browser cache if missing (IP/browser/OS for LogSession)
  if (!readBrowserInfo()) {
    cacheService.getBrowserInfoFromCache().subscribe();
  }

  // ---------------------------
  // 3. ADD AUTH + LOG SESSION HEADERS
  // ---------------------------
  const authReq = withAuthAndLogHeaders(req, token);

  // ---------------------------
  // 4. HANDLE REQUEST
  // ---------------------------
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {

      // ---------------------------
      // 401 HANDLING (REFRESH TOKEN)
      // ---------------------------
      if (
        error.status === 401 &&
        !req.url.includes('accounts/login') &&
        !req.url.includes('accounts/refresh-token')
      ) {

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          const auth = authService.getAuthFromLocalStorage();

          if (auth) {
            return accountService.refreshToken().pipe(
              switchMap((newAuth: AuthModel) => {

                isRefreshing = false;

                authService.setAuthFromLocalStorage(newAuth);
                refreshTokenSubject.next(newAuth.authToken);

                const retryReq = withAuthAndLogHeaders(req, newAuth.authToken);

                return next(retryReq);
              }),
              catchError(err => {
                isRefreshing = false;
                refreshTokenSubject.next(null);

                ngZone.run(() => {
                  chunkErrorHandler.handleHttpErrorByStatus(401);
                });

                return throwError(() => err);
              })
            );
          }
        }

        // WAIT FOR TOKEN REFRESH
        return refreshTokenSubject.pipe(
          filter(t => t !== null),
          take(1),
          switchMap(t => {
            const retryReq = withAuthAndLogHeaders(req, t!);
            return next(retryReq);
          })
        );
      }

      // ---------------------------
      // OTHER ERRORS
      // ---------------------------
      return throwError(() => error);
    })
  );
};
